/** @jsxImportSource @emotion/react */
import { propertyIdPresenter } from '@kaiizen/kai-presenters';
import { ApiPropertyProject } from '@kaiizen/kai-pro-api';
import {
  useAuth,
  useIsOffline,
  useKaiProApiClient,
  useMarketsByUserId,
  useOrgMarkets,
  usePropertyProjects,
} from '@kaiizen/kai-pro-api-react-state';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useDebounce } from 'use-debounce';

import { ANALYTICS_EVENTS, trackEvent } from '@/Client/analytics';
import { NoRecordsPlaceholder } from '@/Client/Partials';
import { CommonField } from '@kaiizen/kai-shiki/react';
import { PageLayout } from '../../Layout';
import { usePageTitle, usePersistedState, usePropertyIdLabel } from '../../Lib';
import { useConfirmationModal, useToast } from '../../State';
import { AddMarketsFlyout } from './AddMarketsFlyout';
import {
  getDateNumber,
  isProjectArchived,
  projectNeedsAttention,
  SearchCloseIcon,
  SearchIcon,
} from './dashboardHelpers';
import { DashboardProjectRow } from './DashboardProjectRow';
import {
  DashboardSortDropdown,
  type DashboardSortOption,
} from './DashboardSortDropdown';
import {
  ATTENTION_BADGE_STYLE,
  CARD_LIST_STYLE,
  CONTENT_STYLE,
  DASHBOARD_TAB_ACTIVE_STYLE,
  DASHBOARD_TAB_STYLE,
  DASHBOARD_TABS_STYLE,
  EMPTY_STATE_STYLE,
  HEADER_COPY_STYLE,
  LOAD_MORE_STYLE,
  MAIN_STYLE,
  OFFLINE_STYLE,
  OVERVIEW_DIVIDER_STYLE,
  OVERVIEW_SUBTITLE_STYLE,
  OVERVIEW_TITLE_ROW_STYLE,
  OVERVIEW_TITLE_STYLE,
  PAGE_STYLE,
  SEARCH_ACTIONS_STYLE,
  SEARCH_CLEAR_BUTTON_STYLE,
  SEARCH_COLLAPSED_BUTTON_STYLE,
  SEARCH_FIELD_BOX_STYLE,
  SEARCH_FIELD_ICON_STYLE,
  SEARCH_FIELD_LABEL_STYLE,
  SEARCH_FIELD_STYLE,
  SEARCH_FIELD_VALUE_STYLE,
  SEARCH_STYLE,
  SIDEBAR_ADD_MARKET_BUTTON_STYLE,
  SIDEBAR_ITEM_ACTIVE_STYLE,
  SIDEBAR_ITEM_COUNTS_STYLE,
  SIDEBAR_ITEM_NAME_STYLE,
  SIDEBAR_ITEM_STYLE,
  SIDEBAR_MARKETS_LIST_STYLE,
  SIDEBAR_SECTION_TITLE_STYLE,
  SIDEBAR_STYLE,
  SIDEBAR_TOTAL_COUNT_STYLE,
  SORT_WRAPPER_STYLE,
  TOPBAR_STYLE,
} from './dashboardStyles';
import { DashboardView, SortMode } from './dashboardTypes';

const ALL_MARKETS_ID = 'all';
const INITIAL_PAGE_SIZE = 25;
const QUERY_PAGE_BUFFER = 25;

const normalizeDashboardSortText = (value?: string | null) => {
  const normalizedValue = value?.trim() ?? '';
  return normalizedValue === '--' ? '' : normalizedValue;
};

const compareDashboardText = (left?: string | null, right?: string | null) => {
  const normalizedLeft = normalizeDashboardSortText(left);
  const normalizedRight = normalizeDashboardSortText(right);

  if (!normalizedLeft && !normalizedRight) return 0;
  if (!normalizedLeft) return 1;
  if (!normalizedRight) return -1;

  return normalizedLeft.localeCompare(normalizedRight, undefined, {
    numeric: true,
    sensitivity: 'base',
  });
};

export const RedesignedDashboardPage = () => {
  usePageTitle('My Dashboard');
  const navigate = useNavigate();
  const { id: routeMarketId } = useParams();
  const { user, userCan } = useAuth();
  const canManageDashboardMarkets = userCan('update', 'OrgMarket');
  const kaiProApiClient = useKaiProApiClient();
  const propertyIdLabel = usePropertyIdLabel();
  const {
    markets: allOrgMarkets,
    isLoading: isLoadingAllOrgMarkets,
    marketsMutators,
  } = useOrgMarkets();
  const confirm = useConfirmationModal();
  const { showToast, tryWithErrorToast } = useToast();
  const isOffline = useIsOffline();

  const [dashboardView, setDashboardView] = usePersistedState<DashboardView>('dashboard:view', 'active');
  const [sortMode, setSortMode] = usePersistedState<SortMode>('dashboard:sortMode', 'needsAttention');
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [isAddMarketsOpen, setIsAddMarketsOpen] = useState(false);
  const [isSavingMarkets, setIsSavingMarkets] = useState(false);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [searchValue, setSearchValue] = usePersistedState('dashboard:search', '');
  const [visibleCount, setVisibleCount] = useState(INITIAL_PAGE_SIZE);
  const [actionProjectId, setActionProjectId] = useState<string>();
  const [debouncedSearch] = useDebounce(searchValue.trim(), 250);
  const sortRef = useRef<HTMLDivElement>(null);

  const {
    markets,
    isLoading: isLoadingMarkets,
    refetch: refetchAssignedMarkets,
  } = useMarketsByUserId(user?.id);
  const selectedMarketId = routeMarketId ?? ALL_MARKETS_ID;
  const assignedMarketIds = useMemo(
    () => (markets ?? []).map(market => market.id),
    [markets],
  );
  const selectedOrgMarketIds = useMemo(() => {
    if (assignedMarketIds.length === 0) return [];
    if (selectedMarketId === ALL_MARKETS_ID) return assignedMarketIds;
    return assignedMarketIds.includes(selectedMarketId)
      ? [selectedMarketId]
      : [];
  }, [assignedMarketIds, selectedMarketId]);
  const listQueryLimit = Math.max(
    visibleCount + QUERY_PAGE_BUFFER,
    INITIAL_PAGE_SIZE,
  );
  const searchQueryLimit = Math.max(visibleCount + QUERY_PAGE_BUFFER, 50);

  // One list query, reconfigured for whatever the user is currently looking at,
  // rather than one query per tab fired up front. The cache is keyed by these
  // params, so switching tabs (or back again) re-uses what was already fetched.
  const listView = debouncedSearch ? 'search' : dashboardView;
  const {
    propertyProjects,
    isLoading: isLoadingProjects,
    propertyProjectMutators,
  } = usePropertyProjects({
    limit: listView === 'search' ? searchQueryLimit : listQueryLimit,
    orgMarketIds: selectedOrgMarketIds,
    search: debouncedSearch || undefined,
    status: listView === 'closed'
      ? 'completed'
      : listView === 'active'
      ? 'active'
      : undefined,
    includeArchived: listView === 'search' || listView === 'archived' || undefined,
    sortBy: listView === 'closed' || listView === 'archived' ? undefined : 'relevance',
    // selectedOrgMarketIds is empty until the user's markets arrive; querying
    // before then just fetches an empty list and then refetches for real.
    enabled: !!markets,
  });

  useEffect(() => {
    if (!markets || routeMarketId) return;
    navigate(`/dashboard/market/${ALL_MARKETS_ID}`, { replace: true });
  }, [markets, navigate, routeMarketId]);

  useEffect(() => {
    if (!markets || selectedMarketId === ALL_MARKETS_ID) return;
    if (!assignedMarketIds.includes(selectedMarketId)) {
      navigate(`/dashboard/market/${ALL_MARKETS_ID}`, { replace: true });
    }
  }, [assignedMarketIds, markets, navigate, selectedMarketId]);

  useEffect(() => {
    if (dashboardView !== 'active' && sortMode === 'needsAttention') {
      setSortMode('lastUpdated');
    }
  }, [dashboardView, sortMode, setSortMode]);

  useEffect(() => {
    setVisibleCount(INITIAL_PAGE_SIZE);
  }, [dashboardView, sortMode, selectedMarketId, debouncedSearch]);

  useEffect(() => {
    if (!debouncedSearch && searchValue.trim() === '') return;
    if (debouncedSearch) return;
    if (selectedMarketId !== ALL_MARKETS_ID) {
      navigate(`/dashboard/market/${ALL_MARKETS_ID}`, { replace: true });
    }
  }, [debouncedSearch, navigate, searchValue, selectedMarketId]);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (!sortRef.current?.contains(event.target as Node)) setIsSortOpen(false);
    };

    if (isSortOpen) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [isSortOpen]);

  const sortedAvailableMarkets = useMemo(
    () =>
      [...(allOrgMarkets ?? [])]
        .filter(market => !market.archivedAt)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [allOrgMarkets],
  );
  const sortedMarkets = useMemo(
    () => sortedAvailableMarkets.filter(market => assignedMarketIds.includes(market.id)),
    [assignedMarketIds, sortedAvailableMarkets],
  );
  const selectedMarket = useMemo(
    () => sortedMarkets.find(market => market.id === selectedMarketId),
    [selectedMarketId, sortedMarkets],
  );
  const marketNameById = useMemo(
    () => new Map(sortedMarkets.map(market => [market.id, market.name])),
    [sortedMarkets],
  );
  const projectsSafe = useMemo(() => propertyProjects ?? [], [propertyProjects]);

  const marketAttentionCountById = useMemo(() => {
    return new Map(
      sortedMarkets.map(market => [
        market.id,
        market.needsAttentionProjectCount ?? 0,
      ]),
    );
  }, [sortedMarkets]);

  const activeCountByMarketId = useMemo(() => {
    return new Map(
      sortedMarkets.map(market => [market.id, market.activeProjectCount ?? 0]),
    );
  }, [sortedMarkets]);

  const closedCountByMarketId = useMemo(
    () =>
      new Map(
        sortedMarkets.map(market => [market.id, market.closedProjectCount ?? 0]),
      ),
    [sortedMarkets],
  );
  const archivedCountByMarketId = useMemo(
    () =>
      new Map(
        sortedMarkets.map(market => [
          market.id,
          market.archivedProjectCount ?? 0,
        ]),
      ),
    [sortedMarkets],
  );

  // Search results are shown exactly as the server ranked them; the tab views are
  // narrowed to the selected market and archive state is the project's timestamp.
  const projectsForCurrentView = useMemo(() => {
    if (debouncedSearch) return projectsSafe;

    const forView = dashboardView === 'archived'
      ? projectsSafe.filter(isProjectArchived)
      : dashboardView === 'closed'
      ? projectsSafe.filter(project => !isProjectArchived(project))
      : projectsSafe;

    if (selectedMarketId === ALL_MARKETS_ID) return forView;
    return forView.filter(
      project => project.orgProperty?.orgMarketId === selectedMarketId,
    );
  }, [dashboardView, debouncedSearch, projectsSafe, selectedMarketId]);

  const currentProjects = useMemo(() => {
    if (debouncedSearch) return [...projectsForCurrentView];

    let filtered = [...projectsForCurrentView];

    filtered.sort((a, b) => {
      if (sortMode === 'address') {
        return compareDashboardText(
          a.orgProperty?.address,
          b.orgProperty?.address,
        );
      }

      if (sortMode === 'orgPropertyId') {
        return compareDashboardText(
          propertyIdPresenter(a.orgProperty),
          propertyIdPresenter(b.orgProperty),
        );
      }

      if (sortMode === 'market') {
        return compareDashboardText(
          marketNameById.get(a.orgProperty?.orgMarketId ?? ''),
          marketNameById.get(b.orgProperty?.orgMarketId ?? ''),
        );
      }

      if (sortMode === 'projectDueDate') {
        const dueDelta = getDateNumber(
          a.latestScan?.customerMetadata?.dueDate
            ?? a.firstScan?.customerMetadata?.dueDate,
        )
          - getDateNumber(
            b.latestScan?.customerMetadata?.dueDate
              ?? b.firstScan?.customerMetadata?.dueDate,
          );
        return (
          dueDelta || getDateNumber(b.updatedAt) - getDateNumber(a.updatedAt)
        );
      }

      if (sortMode === 'lastUpdated') {
        return getDateNumber(b.updatedAt) - getDateNumber(a.updatedAt);
      }

      const attentionDelta = Number(projectNeedsAttention(b)) - Number(projectNeedsAttention(a));
      if (attentionDelta !== 0) return attentionDelta;

      const dueDelta = getDateNumber(
        a.latestScan?.customerMetadata?.dueDate
          ?? a.firstScan?.customerMetadata?.dueDate,
      )
        - getDateNumber(
          b.latestScan?.customerMetadata?.dueDate
            ?? b.firstScan?.customerMetadata?.dueDate,
        );
      if (dueDelta !== 0) return dueDelta;

      return getDateNumber(b.updatedAt) - getDateNumber(a.updatedAt);
    });

    return filtered;
  }, [debouncedSearch, marketNameById, projectsForCurrentView, sortMode]);

  const visibleProjects = currentProjects.slice(0, visibleCount);
  const needsAttentionCount = selectedMarketId === ALL_MARKETS_ID
    ? Array.from(marketAttentionCountById.values()).reduce(
      (sum, count) => sum + count,
      0,
    )
    : (marketAttentionCountById.get(selectedMarketId) ?? 0);
  const overviewNeedsAttentionCount = useMemo(
    () =>
      Array.from(marketAttentionCountById.values()).reduce(
        (sum, count) => sum + count,
        0,
      ),
    [marketAttentionCountById],
  );
  const sidebarTotalActive = useMemo(
    () =>
      Array.from(activeCountByMarketId.values()).reduce(
        (sum, count) => sum + count,
        0,
      ),
    [activeCountByMarketId],
  );
  const activeCount = selectedMarketId === ALL_MARKETS_ID
    ? sidebarTotalActive
    : (activeCountByMarketId.get(selectedMarketId) ?? 0);
  const closedCountFromMarkets = selectedMarketId === ALL_MARKETS_ID
    ? Array.from(closedCountByMarketId.values()).reduce(
      (sum, count) => sum + count,
      0,
    )
    : (closedCountByMarketId.get(selectedMarketId) ?? 0);
  const archivedCountFromMarkets = selectedMarketId === ALL_MARKETS_ID
    ? Array.from(archivedCountByMarketId.values()).reduce(
      (sum, count) => sum + count,
      0,
    )
    : (archivedCountByMarketId.get(selectedMarketId) ?? 0);
  const closedCount = closedCountFromMarkets;
  const archivedCount = archivedCountFromMarkets;
  const totalProjectsForCurrentView = debouncedSearch
    ? currentProjects.length
    : dashboardView === 'closed'
    ? closedCount
    : dashboardView === 'archived'
    ? archivedCount
    : activeCount;
  const canLoadMore = totalProjectsForCurrentView > visibleCount;
  const nextLoadCount = Math.min(
    INITIAL_PAGE_SIZE,
    Math.max(totalProjectsForCurrentView - visibleCount, 0),
  );
  const isLoading = isLoadingMarkets || isLoadingAllOrgMarkets || isLoadingProjects;

  const sortOptions: DashboardSortOption[] = useMemo(() => {
    if (dashboardView === 'closed') {
      return [
        { mode: 'address', label: 'Address' },
        { mode: 'lastUpdated', label: 'Completed Date' },
        { mode: 'market', label: 'Market Name' },
        { mode: 'orgPropertyId', label: 'Org Property ID' },
      ];
    }

    if (dashboardView === 'archived') {
      return [
        { mode: 'address', label: 'Address' },
        { mode: 'lastUpdated', label: 'Archived Date' },
        { mode: 'market', label: 'Market Name' },
        { mode: 'orgPropertyId', label: 'Org Property ID' },
      ];
    }

    return [
      { mode: 'address', label: 'Address' },
      { mode: 'projectDueDate', label: 'Due Date' },
      { mode: 'lastUpdated', label: 'Last Updated' },
      { mode: 'market', label: 'Market Name' },
      { mode: 'needsAttention', label: 'Needs Review' },
      { mode: 'orgPropertyId', label: 'Org Property ID' },
    ];
  }, [dashboardView]);
  const shouldShowExpandedSearch = isSearchExpanded || !!searchValue;
  const searchPlaceholder = selectedMarketId === ALL_MARKETS_ID
    ? 'Search all markets by address or property ID'
    : 'Search by address or property ID';
  const overviewCount = dashboardView === 'closed'
    ? closedCount
    : dashboardView === 'archived'
    ? archivedCount
    : activeCount;
  const overviewViewLabel = dashboardView === 'closed'
    ? 'completed'
    : dashboardView === 'archived'
    ? 'archived'
    : 'active';
  const overviewSubtitle = selectedMarketId === ALL_MARKETS_ID
    ? `${overviewCount} ${overviewViewLabel} project${overviewCount === 1 ? '' : 's'} across all markets`
    : `${overviewCount} ${overviewViewLabel} project${overviewCount === 1 ? '' : 's'} in ${selectedMarket?.name ?? 'this market'}`;
  const dashboardHeaderTabs = (
    <div css={DASHBOARD_TABS_STYLE}>
      <button
        type='button'
        css={dashboardView === 'active'
          ? DASHBOARD_TAB_ACTIVE_STYLE
          : DASHBOARD_TAB_STYLE}
        onClick={() => {
          setDashboardView('active');
          setSortMode('needsAttention');
        }}
      >
        {`Active (${activeCount})`}
      </button>
      <button
        type='button'
        css={dashboardView === 'closed'
          ? DASHBOARD_TAB_ACTIVE_STYLE
          : DASHBOARD_TAB_STYLE}
        onClick={() => setDashboardView('closed')}
      >
        {`Completed (${closedCount})`}
      </button>
      <button
        type='button'
        css={dashboardView === 'archived'
          ? DASHBOARD_TAB_ACTIVE_STYLE
          : DASHBOARD_TAB_STYLE}
        onClick={() => setDashboardView('archived')}
      >
        {`Archived (${archivedCount})`}
      </button>
    </div>
  );

  const handleArchiveProject = (project: ApiPropertyProject) => {
    confirm({
      title: 'Archive Project',
      message: 'Are you sure you want to archive this project?',
      confirmLabel: 'Archive',
      onConfirm: async () => {
        setActionProjectId(project.id);
        await tryWithErrorToast('archiving project', async () => {
          await propertyProjectMutators.update(project.id, {
            archivedAt: new Date().toISOString(),
          });
          trackEvent(ANALYTICS_EVENTS.projectArchived, { projectId: project.id });
        });
        await marketsMutators.refetchAllMarkets();
        setActionProjectId(undefined);
      },
    });
  };

  const handleReactivateProject = async (project: ApiPropertyProject) => {
    setActionProjectId(project.id);
    await tryWithErrorToast('reactivating project', async () => {
      await propertyProjectMutators.update(project.id, { status: 'active' });
      trackEvent(ANALYTICS_EVENTS.projectUpdated, { projectId: project.id, field: 'reactivated' });
    });
    await marketsMutators.refetchAllMarkets();
    setActionProjectId(undefined);
  };

  const handleUnarchiveProject = async (project: ApiPropertyProject) => {
    setActionProjectId(project.id);
    await tryWithErrorToast('unarchiving project', async () => {
      await propertyProjectMutators.update(project.id, { archivedAt: null });
      trackEvent(ANALYTICS_EVENTS.projectUnarchived, { projectId: project.id });
    });
    await marketsMutators.refetchAllMarkets();
    setActionProjectId(undefined);
  };

  const handleSaveAssignedMarkets = async (marketIds: string[]) => {
    if (!user?.id) return;

    const marketsToAdd = marketIds.filter(id => !assignedMarketIds.includes(id));
    const marketsToRemove = assignedMarketIds.filter(
      id => !marketIds.includes(id),
    );

    if (marketsToAdd.length === 0 && marketsToRemove.length === 0) {
      setIsAddMarketsOpen(false);
      return;
    }

    setIsSavingMarkets(true);
    let didSucceed = false;
    await tryWithErrorToast('updating dashboard markets', async () => {
      await Promise.all([
        ...marketsToAdd.map(marketId => kaiProApiClient.addUserToOrgMarket(marketId, user.id)),
        ...marketsToRemove.map(marketId => kaiProApiClient.removeUserFromOrgMarket(marketId, user.id)),
      ]);
      await refetchAssignedMarkets();
      didSucceed = true;
    });
    setIsSavingMarkets(false);

    if (!didSucceed) return;

    showToast('Dashboard markets updated', 'success');
    setIsAddMarketsOpen(false);
  };

  if (
    !isLoading
    && !isLoadingAllOrgMarkets
    && sortedAvailableMarkets.length === 0
  ) {
    return (
      <PageLayout
        title='Dashboard'
        headerButtons={dashboardHeaderTabs}
        contentLayoutStyling='none'
      >
        <NoRecordsPlaceholder
          recordType='Markets'
          description='Start by creating your first market.'
          createButtonText='Create Market'
          createFirstRecord={() => navigate('/market/new')}
          iconType='dashboard'
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title='Dashboard'
      headerButtons={dashboardHeaderTabs}
      contentLayoutStyling='none'
    >
      <div css={PAGE_STYLE}>
        {canManageDashboardMarkets && (
          <AddMarketsFlyout
            open={isAddMarketsOpen}
            availableMarkets={sortedAvailableMarkets}
            initialSelectedMarketIds={assignedMarketIds}
            isSaving={isSavingMarkets}
            onClose={() => setIsAddMarketsOpen(false)}
            onSave={handleSaveAssignedMarkets}
          />
        )}
        <div css={SIDEBAR_STYLE}>
          <div css={SIDEBAR_SECTION_TITLE_STYLE}>My Dashboard</div>
          <div
            css={[
              selectedMarketId === ALL_MARKETS_ID
                ? SIDEBAR_ITEM_ACTIVE_STYLE
                : SIDEBAR_ITEM_STYLE,
              selectedMarketId === ALL_MARKETS_ID && {
                backgroundColor: '#dfdfdf',
              },
            ]}
            onClick={() => navigate(`/dashboard/market/${ALL_MARKETS_ID}`)}
          >
            <span css={SIDEBAR_ITEM_NAME_STYLE}>Overview</span>
            <span css={SIDEBAR_ITEM_COUNTS_STYLE}>
              {overviewNeedsAttentionCount > 0 && <span css={ATTENTION_BADGE_STYLE} />}
              <span css={SIDEBAR_TOTAL_COUNT_STYLE}>{sidebarTotalActive}</span>
            </span>
          </div>

          <div css={[SIDEBAR_SECTION_TITLE_STYLE, { marginTop: 18 }]}>
            <span>{`Markets (${sortedMarkets.length})`}</span>
          </div>
          <div css={SIDEBAR_MARKETS_LIST_STYLE}>
            {sortedMarkets.map(market => {
              const attentionCount = marketAttentionCountById.get(market.id) ?? 0;
              const activeProjectCount = activeCountByMarketId.get(market.id) ?? 0;
              const isSelected = selectedMarketId === market.id;
              return (
                <div
                  key={market.id}
                  css={[
                    isSelected ? SIDEBAR_ITEM_ACTIVE_STYLE : SIDEBAR_ITEM_STYLE,
                    isSelected && { backgroundColor: '#dfdfdf' },
                  ]}
                  onClick={() => navigate(`/dashboard/market/${market.id}`)}
                >
                  <span css={SIDEBAR_ITEM_NAME_STYLE}>{market.name}</span>
                  <span css={SIDEBAR_ITEM_COUNTS_STYLE}>
                    {attentionCount > 0 && <span css={ATTENTION_BADGE_STYLE} />}
                    <span css={SIDEBAR_TOTAL_COUNT_STYLE}>
                      {activeProjectCount > 0 ? activeProjectCount : '--'}
                    </span>
                  </span>
                </div>
              );
            })}
          </div>
          {canManageDashboardMarkets && (
            <button
              type='button'
              css={SIDEBAR_ADD_MARKET_BUTTON_STYLE}
              onClick={() => setIsAddMarketsOpen(true)}
            >
              <span>+</span>
              <span>ADD MARKET</span>
            </button>
          )}
        </div>

        <div css={MAIN_STYLE}>
          {isOffline && (
            <div css={OFFLINE_STYLE}>
              Offline mode: dashboard data may be stale.
            </div>
          )}

          <div css={CONTENT_STYLE}>
            <div css={TOPBAR_STYLE}>
              <div css={HEADER_COPY_STYLE}>
                {selectedMarketId === ALL_MARKETS_ID
                  ? (
                    <div css={OVERVIEW_TITLE_ROW_STYLE}>
                      <div css={OVERVIEW_TITLE_STYLE}>Overview</div>
                      <div css={OVERVIEW_DIVIDER_STYLE} />
                      <div css={OVERVIEW_SUBTITLE_STYLE}>{overviewSubtitle}</div>
                    </div>
                  )
                  : (
                    <div css={OVERVIEW_TITLE_ROW_STYLE}>
                      <div css={OVERVIEW_TITLE_STYLE}>
                        {selectedMarket?.name
                          ? `${selectedMarket.name} Market`
                          : 'Market'}
                      </div>
                      <div css={OVERVIEW_DIVIDER_STYLE} />
                      <div css={OVERVIEW_SUBTITLE_STYLE}>{overviewSubtitle}</div>
                    </div>
                  )}
              </div>

              <div css={SEARCH_ACTIONS_STYLE}>
                {shouldShowExpandedSearch
                  ? (
                    <div css={SEARCH_STYLE}>
                      <span css={SEARCH_FIELD_ICON_STYLE}>
                        <SearchIcon />
                      </span>
                      <CommonField
                        label='Search'
                        state='focused'
                        style={SEARCH_FIELD_STYLE}
                        boxCss={SEARCH_FIELD_BOX_STYLE}
                        labelCss={SEARCH_FIELD_LABEL_STYLE}
                        valueCss={SEARCH_FIELD_VALUE_STYLE}
                        inputProps={{
                          'aria-label': 'Search projects',
                          autoFocus: true,
                          type: 'search',
                          value: searchValue,
                          onChange: event => setSearchValue(event.target.value),
                          onBlur: () => {
                            if (!searchValue.trim()) setIsSearchExpanded(false);
                          },
                          placeholder: searchPlaceholder,
                        }}
                      />
                      {!!searchValue.trim() && (
                        <button
                          type='button'
                          css={SEARCH_CLEAR_BUTTON_STYLE}
                          onClick={() => {
                            setSearchValue('');
                            setIsSearchExpanded(false);
                          }}
                          aria-label='Clear search'
                        >
                          <SearchCloseIcon />
                        </button>
                      )}
                    </div>
                  )
                  : (
                    <button
                      type='button'
                      css={SEARCH_COLLAPSED_BUTTON_STYLE}
                      onClick={() => setIsSearchExpanded(true)}
                      aria-label='Open search'
                    >
                      <SearchIcon />
                    </button>
                  )}
                <div css={SORT_WRAPPER_STYLE} ref={sortRef}>
                  <DashboardSortDropdown
                    isOpen={isSortOpen}
                    sortMode={sortMode}
                    sortOptions={sortOptions}
                    onToggle={() => setIsSortOpen(open => !open)}
                    onSelect={mode => {
                      setSortMode(mode);
                      setIsSortOpen(false);
                    }}
                  />
                </div>
              </div>
            </div>

            {isLoading ? <div css={EMPTY_STATE_STYLE}>Loading dashboard...</div> : visibleProjects.length === 0
              ? (
                <div css={EMPTY_STATE_STYLE}>
                  {debouncedSearch
                    ? `No projects match "${debouncedSearch}".`
                    : `No ${dashboardView} projects found.`}
                </div>
              )
              : (
                <>
                  <div css={CARD_LIST_STYLE}>
                    {visibleProjects.map(project => (
                      <DashboardProjectRow
                        key={project.id}
                        project={project}
                        marketName={marketNameById.get(
                          project.orgProperty?.orgMarketId ?? '',
                        )}
                        propertyIdLabel={propertyIdLabel}
                        onArchive={handleArchiveProject}
                        onUnarchive={handleUnarchiveProject}
                        onReactivate={handleReactivateProject}
                        actionInFlight={actionProjectId === project.id}
                        onClick={() => navigate(`/project/${project.id}`)}
                      />
                    ))}
                  </div>
                  {canLoadMore && (
                    <button
                      type='button'
                      css={LOAD_MORE_STYLE}
                      onClick={() => setVisibleCount(count => count + INITIAL_PAGE_SIZE)}
                    >
                      LOAD {nextLoadCount} MORE PROJECTS
                    </button>
                  )}
                </>
              )}
          </div>
        </div>
      </div>
    </PageLayout>
  );
};
